import React from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { getAvatarStyle, isNewStyleAvatar } from '../../lib/s3';
import { openSuccessionDrawer } from '../../actions/successionDrawerActionCreators';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import { currencySymbol } from '../../utils/formatters';

const formatCurrencyShort = (value) => {
  const n = Number(value || 0);
  const sym = currencySymbol();
  if (n >= 1_000_000_000) return `${sym}${Math.round(n / 1_000_000_000)}B`;
  if (n >= 1_000_000) return `${sym}${Math.round(n / 1_000_000)}M`;
  if (n >= 1_000) return `${sym}${Math.round(n / 1_000)}K`;
  return `${sym}${Math.round(n)}`;
};

const RolodexUserRow = ({ user, onClick, companySettings }) => {
  const name = user.allowed_name || user.user_name;
  const location = [user.city, user.province].filter(Boolean).join(', ');
  const avatarUrl = user.profile_picture ? getAvatarStyle(user.profile_picture, companySettings).url : undefined;
  const aumText = formatCurrencyShort(user.assets_under_management);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-xl border px-3 py-3 transition-all duration-200 bg-succession-bg-primary border-succession-border hover:bg-succession-hover-bg hover:shadow-succession-sm"
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          {avatarUrl ? (
            <img src={avatarUrl} alt={name} className="w-9 h-9 rounded-full object-cover" style={isNewStyleAvatar(user.profile_picture) ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}} />
          ) : (
            <div className="w-9 h-9 rounded-full bg-succession-bg-secondary border border-succession-border flex items-center justify-center text-succession-text-secondary font-semibold">
              {String(name || '?').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate text-succession-text-primary">{name}</div>
          <div className="text-xs text-succession-text-secondary truncate">{location || '—'}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 mb-1">
            <svg className="w-3 h-3 text-succession-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2" />
            </svg>
            <span className="text-xs text-succession-text-secondary">AUM</span>
          </div>
          <div className="text-sm font-semibold text-succession-text-primary">{aumText}</div>
        </div>
      </div>
    </button>
  );
};

const GET_ROLODEX_USERS = gql`
  query GetRolodexUsers {
    getRolodex {
      id
      user_name
      fb_user_name
      allowed_name
      city
      province
      assets_under_management
      profile_picture
    }
  }
`;

const RolodexMiniCard = ({ showHeader = true }) => {
  const dispatch = useDispatch();
  const { company: companySettings } = useSuccessionTheme();
  const { data, loading, error } = useQuery(GET_ROLODEX_USERS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const users = Array.isArray(data?.getRolodex) ? data.getRolodex.slice(0, 3) : [];
  const rolodexCount = data?.getRolodex?.length || 0;

  return (
    <div className="mt-1 flex-1 flex flex-col">
      {showHeader && (
        <div className="mb-3">
          <h3><FormattedMessage id="succession.rolodex_mini_card.title" /></h3>
          <h4><FormattedMessage id="succession.rolodex_mini_card.contact_count" values={{ count: rolodexCount }} /></h4>
          <p className="text-sm text-succession-success"><FormattedMessage id="succession.rolodex_mini_card.empty_cta" /></p>
        </div>
      )}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-14 rounded bg-succession-bg-secondary animate-pulse border border-succession-border" />
          ))}
        </div>
      ) : error ? (
        <div className="text-succession-error text-sm"><FormattedMessage id="succession.rolodex_mini_card.load_error" /></div>
      ) : users.length === 0 ? (
        <div className="text-succession-text-secondary text-sm"><FormattedMessage id="succession.rolodex_mini_card.no_contacts" /></div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <RolodexUserRow
              key={u.id}
              user={u}
              companySettings={companySettings}
              onClick={() => dispatch(openSuccessionDrawer(u.fb_user_name || u.user_name))}
            />
          ))}
        </div>
      )}
      <div className="mt-3 pt-2 flex justify-center mt-auto">
        <a href="/succession/rolodex" className="text-sm text-succession-primary hover:text-succession-primary-hover visited:text-succession-primary">
          <FormattedMessage id="succession.rolodex_mini_card.view_all" />
        </a>
      </div>
    </div>
  );
};

export default RolodexMiniCard;

